import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as wlcFilterId } from '../../filters/wireless-controller-filter/WirelessControllerFilter';
import { filterId as accessPointFilterId } from '../../filters/access-point-filter/AccessPointFilter';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { listTooltip } from './funcs/IpmList';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';
import { siteFilterId, statusFilterId, timeRangeFilterId, wirelessControllerFilterId } from '../../filters';

const title = t`Access Points`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'device',
        deviceType: 'accesspoint',
        entityFields: [
            'name',
            'location',
            'deviceType',
            'channels',
            'wirelessController',
            'wirelessControllerName',
            'description',
        ],
        metrics: ['status', 'channels', 'deviceInterfaceStatus'],
        trends: ['status'],
        renderAsEntity: true,
    },
};

const columns = [
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Name`,
        sortable: true,
        mapping: 'device.name',
        getTooltip: listTooltip('device.name', 'device.description'),
        width: 200,
        link: {
            target: '/access-point-detail',
            mappedFilterParams: {
                [accessPointFilterId]: 'device',
            },
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Sites`,
        sortable: false,
        mapper: (row) => {
            const sites = row.device.sites || [];
            if (sites.length === 0) return DEFAULT_INVALID_VALUE;
            return sites.map((site) => site.name).join(', ');
        },
        width: 120,
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`Wireless Controller`,
        sortable: true,
        mapping: 'device.wirelessControllerName',
        getTooltip: listTooltip('device.wirelessControllerName'),
        width: 170,
        link: {
            target: '../wireless-controller-detail',
            mappedFilterParams: {
                [wlcFilterId]: (row) => ({ uuid: row.device.wirelessController }),
            },
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Location`,
        sortable: true,
        mapping: 'device.location',
        width: 120,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Channels`,
        sortable: true,
        sortProperty: 'channels',
        mapper: (row) => {
            const channels = row.channels || [];
            if (channels.length === 0) return DEFAULT_INVALID_VALUE;
            return channels.map((channel) => channel.channelNumber).join(', ');
        },
        width: 120,
    },
    {
        type: COLUMN_TYPES.STATUS,
        title: t`Interface Status`,
        sortable: true,
        mapping: 'deviceInterfaceStatus',
        width: 150,
    },
    {
        type: COLUMN_TYPES.STATUSTREND,
        title: t`Status Trend`,
        sortable: true,
        sortProperty: 'status',
        mapping: 'trends.status.data',
        flex: 1,
    },
];

const sorters = [{ property: 'status', direction: 'desc' }];

function AccessPointListPanel(props) {
    const loader = useQueryLoader(dataSource);
    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="device.deleted"
        />
    );
}

AccessPointListPanel.propTypes = panelPropTypes;

AccessPointListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'AccessPointList',
    catalogEntry: {
        category: PANEL_CATEGORIES.ACCESS_POINT,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`List of wireless access points (APs), showing status trends`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [siteFilterId, statusFilterId, wirelessControllerFilterId],
    },
})(AccessPointListPanel);
